import type { ResultadoBusqueda } from '../services/tariff-catalog-service';

/**
 * Los resultados, puestos dentro de la rama del arancel a la que pertenecen.
 *
 * La lista plana no dice por qué la primera partida está primera: quince filas
 * sueltas son quince preguntas. Agrupadas por capítulo y por partida de cuatro
 * dígitos son tres o cuatro ramas, y la pregunta pasa a ser "¿cuál de estas
 * ramas es mi producto?", que es la que el arancel realmente hace.
 */

/** Una partida de 4 dígitos con las subpartidas que matchearon. */
export interface GrupoPartida {
  /** Los cuatro dígitos: `6403`. */
  codigo: string;
  /** El texto del libro para esa partida, sacado de la raíz del contexto. */
  titulo: string;
  filas: ResultadoBusqueda[];
}

export interface GrupoCapitulo {
  capitulo: string;
  nombre: string | null;
  partidas: GrupoPartida[];
  /** Cuántas filas cayeron en el capítulo, sumando todas sus partidas. */
  total: number;
}

export interface ResultadosAgrupados {
  /** Los capítulos que SON la cosa buscada. */
  principales: GrupoCapitulo[];
  /**
   * Los capítulos que solo la mencionan —betún PARA calzado, máquinas PARA
   * fabricar calzado—. Se muestran plegados al final: no compiten de igual a
   * igual con el calzado, pero tampoco se esconden, porque a veces el producto
   * del usuario es justamente el betún.
   */
  otrasMenciones: GrupoCapitulo[];
  /**
   * No quedó ningún capítulo principal y las menciones se promovieron para que
   * la pantalla no quede vacía. Lo que está arriba es lo más cercano que hay,
   * no una coincidencia plena, y la pantalla lo tiene que decir.
   */
  ningunoEsLaCosa: boolean;
}

/** Los cuatro dígitos de la partida: `6403.40.00.00` → `6403`. */
export function codigoDePartida(code: string): string {
  return code.replace(/\./g, '').slice(0, 4);
}

/**
 * Lo que la fila agrega sobre su rama — el último tramo del contexto.
 * Es lo que hay que leer para elegir entre hermanas: el resto ya lo dice el
 * título del grupo.
 */
export function textoPropio(fila: ResultadoBusqueda): string {
  const contexto = fila.fullContext?.trim();
  if (!contexto) return fila.description;
  const tramos = contexto.split('>').map((t) => t.trim());
  return tramos[tramos.length - 1] || fila.description;
}

/** La raíz del contexto: el texto de la partida de 4 dígitos. */
function tituloDeRama(fila: ResultadoBusqueda): string {
  const contexto = fila.fullContext?.trim();
  if (!contexto) return fila.description;
  return contexto.split('>')[0].trim() || fila.description;
}

export function agruparResultados(resultados: ResultadoBusqueda[]): ResultadosAgrupados {
  const capitulos = new Map<string, GrupoCapitulo>();
  // El primero de cada capítulo es el mejor: el ranking ya ordenó, y acá no se
  // reordena nada. Con él se decide si el capítulo entero es la cosa o solo la
  // menciona.
  //
  // 🔴 **Esta regla está REPLICADA en el backend**, en `clasificarCapitulos` de
  // `packages/backend/scripts/arancel/medir-lista-control.ts`: el medidor de los
  // 30 casos la necesita para reportar cuántas veces el capítulo correcto queda
  // como principal, y no puede importar código del frontend.
  // ⚖️ Son dos implementaciones que pueden desincronizarse **en silencio**: si se
  // cambia una, se cambia la otra **en el mismo commit**.
  const soloMenciona = new Map<string, boolean>();

  for (const fila of resultados) {
    let capitulo = capitulos.get(fila.chapter);
    if (!capitulo) {
      capitulo = { capitulo: fila.chapter, nombre: fila.chapterName, partidas: [], total: 0 };
      capitulos.set(fila.chapter, capitulo);
      // `undefined` no degrada: en el camino por código y en el textual simple
      // nadie midió `esLaCosa`, y esconder resultados por una señal que no se
      // tomó sería castigarlos por algo que no se sabe.
      soloMenciona.set(fila.chapter, fila.esLaCosa === false);
    }

    const codigo = codigoDePartida(fila.code);
    let partida = capitulo.partidas.find((p) => p.codigo === codigo);
    if (!partida) {
      partida = { codigo, titulo: tituloDeRama(fila), filas: [] };
      capitulo.partidas.push(partida);
    }
    partida.filas.push(fila);
    capitulo.total += 1;
  }

  const todos = [...capitulos.values()];
  const principales = todos.filter((c) => !soloMenciona.get(c.capitulo));
  const otrasMenciones = todos.filter((c) => soloMenciona.get(c.capitulo));

  // El respaldo: si ningún capítulo quedó como principal, las menciones suben.
  // Pasó en producción con `taladro eléctrico de mano` —50 resultados, la
  // partida correcta 4ª— y la pantalla no mostró ni uno: todo cayó en un
  // plegado que además le decía al usuario que eso NO era lo que buscaba.
  // No se puede afirmar eso cuando es lo único que hay.
  if (principales.length === 0 && otrasMenciones.length > 0) {
    return { principales: otrasMenciones, otrasMenciones: [], ningunoEsLaCosa: true };
  }

  return { principales, otrasMenciones, ningunoEsLaCosa: false };
}
